/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { ApolloError } from "@apollo/client";
import { useMsal } from "@azure/msal-react";
import { Avatar, Box, Button, CircularProgress, Slider, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from "@mui/material";
import { Multiselect } from "multiselect-react-dropdown";
import React, { useContext, useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { HelperTextLabel, WrapperDiv } from "../components/accountSettings/profile";
// @ts-ignore
import UploadWidget from "../components/cloudinary/UploadWidget";
import PlanPricingTableComponent, { Plan, PLAN_TYPE } from "../components/planPricingTable";
import { MultiSelectOption } from "../components/shared/formUtility";
import NavigateBack from "@mui/icons-material/NavigateBefore";
import {
  ChangeAvatarVersionMutation,
  CompleteRegistrationMutation,
  GenreDto,
  ListGenresQuery,
  ProcessRegistrationPlanMutation,
  RegisterUserMutation,
  UpdateUserGenresMutation,
  useChangeAvatarVersionMutation,
  useCompleteRegistrationMutation,
  useListGenresLazyQuery,
  useProcessRegistrationPlanMutation,
  useRegisterUserMutation,
  useUpdateUserGenresMutation,
} from "../generated";
import { useScript } from "../hooks/useScript";
import { UserContext } from "../ServiceWrapper";
import IframeResizer from "iframe-resizer-react";
import EditableMultiSelectInputComponent, { ZMulti } from "../components/shared/editableMultiSelectInput";
import ToolTipComponent from "../components/shared/toolTip";

interface RegisterPageProps {
  /*hasAccessToken: Boolean,
    isAuthenticated: Boolean*/
}

const RegisterPage: React.FC<RegisterPageProps> = ({}: RegisterPageProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));

  let [searchParams, setSearchParams] = useSearchParams();
  const { instance, accounts } = useMsal();
  const [userCreated, setUserCreated] = useState<boolean>(false);
  const [processingRegistration, setProcessingRegistration] = useState<boolean>(false);
  const [genres, setGenres] = useState<MultiSelectOption[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [subscriptionPlanURL, setSubscriptionPlanURL] = useState<string>("");
  const [subscriptionPlanURLBase, setSubscriptionPlanURLBase] = useState<string>("");
  const [subscriptionPlan, setSubscriptionPlan] = useState<Plan | null>(null);
  const [addOnQuantity, setAddOnQuantity] = useState<number | null>(null);
  const [selectedAddOnQuantity, setSelectedAddOnQuantity] = useState<number>(1);

  const widgetStatus = useScript("https://widget.cloudinary.com/v2.0/global/all.js");

  const { hasToken, isAuthenticated, globalCache, currentUser, setCurrentUser } = useContext(UserContext);

  const avatarURL = useMemo(() => {
    let url = `${process.env.REACT_APP_CLOUDINARY_URL}c_scale,h_400,w_400/v${currentUser?.avatarVersion}/users/${currentUser?.userIDHash}/avatar.jpg`;
    return url;
  }, [currentUser]);

  const hostedPageId = searchParams.get("hostedpage_id");

  const [processRegistrationPlan] = useProcessRegistrationPlanMutation();

  const [registerUser, { data: registerUserData, loading: registerUserLoading, error: registerUserError }] = useRegisterUserMutation();

  const [changeAvatarVersion] = useChangeAvatarVersionMutation();

  const [updateUserGenres] = useUpdateUserGenresMutation();

  const [completeRegistration] = useCompleteRegistrationMutation();

  const [listGenres] = useListGenresLazyQuery({
    onCompleted(data: ListGenresQuery) {
      console.log("list genres success");
      console.log(data);
      let options: MultiSelectOption[] = [];
      data.listGenres.map((val: GenreDto, idx: number) => {
        options.push({ id: val.genreIDHash, name: val.name });
      });

      setGenres(options);
      /*setCurrentUser(data?.currentUser);

      if (data?.currentUser === null){
        
        toast.error("An unknown error has occurred while logging you in. Please try signing in again.");
        setTimeout(() => {
          apolloClient.resetStore();
          instance.logoutRedirect();
        }, 3000);
      }
      else if (data?.currentUser?.registrationComplete !== true)
        navigate('/register');
      else
        navigate('/');*/
    },
    onError(error: ApolloError) {
      console.log("an error occurred");
      console.log(error);
    },
  });

  const onCompleteRegistration = () => {
    
    if (processingRegistration)
      return;

    if (currentUser?.avatarVersion == null) {
      toast.error("You must upload an avatar first.");
      return;
    }

    if (selectedGenres.length == 0) {
      toast.error("You must select at least one genre you are interested in first.");
      return;
    }
    setProcessingRegistration(true);

    updateUserGenres({
      variables: {
        genreIDHashes: selectedGenres,
      },
      onCompleted(data: UpdateUserGenresMutation) {
        if (data?.updateUserGenres?.error) {
          toast.error(data?.updateUserGenres.error);
          setProcessingRegistration(false);
          return;
        }

        completeRegistration({
          onCompleted(cRegData: CompleteRegistrationMutation) {
            
            setProcessingRegistration(false);

            if (cRegData?.completeRegistration == null) {
              toast.error("An error occurred while completing your registration, please try again.");
              return;
            }

            setCurrentUser(cRegData.completeRegistration);
            toast.success("Registration Complete!");
          },
          onError(error: ApolloError) {
            setProcessingRegistration(false);
            toast.error("An error occurred while completing your registration, please try again.");
            console.log(error);
          },
        });
      },
      onError(error: ApolloError) {
        setProcessingRegistration(false);
        toast.error("An error occurred while uploading your avatar, please try again.");
        console.log(error);
      },
    });
  };

  const onSelect = (selectedList: any, selectedItem: any) => {
    updateSelectedValues(selectedList);
  };

  const onRemove = (selectedList: any, removedItem: any) => {
    updateSelectedValues(selectedList);
  };

  const updateSelectedValues = (selectedList: any) => {
    let selectedIds: string[] = [];

    selectedList.map((obj: any, idx: number) => {
      selectedIds.push(obj.id);
    });
    setSelectedGenres(selectedIds);
  };

  const [planType, setPlanType] = useState<PLAN_TYPE>(PLAN_TYPE.YEARLY);

  const handlePlanTypeChange = (event: React.MouseEvent<HTMLElement>, planType: PLAN_TYPE) => {
    setPlanType(planType);
  };

  const handleAddOnQuantityChange = (event: Event, value: number | number[], activeThumb: number) => {
    if (typeof value === "number") setSelectedAddOnQuantity(value);
  };

  const skipOffer = () => {
    setAddOnQuantity(0);
    setSubscriptionPlanURL(
      
      subscriptionPlanURLBase
    );
  };

  const addOffer = () => {
    setAddOnQuantity(selectedAddOnQuantity);
    setSubscriptionPlanURL(
      
      subscriptionPlanURLBase + `&addon_code%5B0%5D=${subscriptionPlan?.registration_offer?.addon_code}&addon_quantity%5B0%5D=${selectedAddOnQuantity}`
    );
  };

  const successCallBack = (result: any) => {
    console.log(result);
    //process.env.REACT_APP_GRAPHQL_URI
    // process.env.REACT_APP_CLOUDINARY_URL
    //let url = `${process.env.REACT_APP_CLOUDINARY_URL}c_scale,h_400,w_400/users/${currentUser?.userIDHash}/avatar.jpg`;
    //setAvatarURL(result.info.eager[1].secure_url);
    changeAvatarVersion({
      variables: {
        avatarVersion: result.info.version,
      },
      onCompleted(data: ChangeAvatarVersionMutation) {
        console.log("changed avatar version successfully");

        if (data?.changeAvatarVersion == null) {
          toast.error("An error occurred while uploading your avatar, please try again.");
        } else {
          toast.success("Successfully uploaded avatar");
          setCurrentUser(data.changeAvatarVersion);
        }
      },
      onError(error: ApolloError) {
        toast.error("An error occurred while uploading your avatar, please try again.");
        console.log(error);
      },
    });
  };

  const failureCallBack = (error: any, result: any) => {};

  useEffect(() => {
    if (hostedPageId == null) {
      if (currentUser == null) {
        console.log("start registering");
        registerUser({
          onCompleted(data: RegisterUserMutation) {
            console.log("registered successfully");

            if (data.registerUser?.error) {
              setTimeout(() => {
                instance.logoutRedirect();
              }, 5000);
            } else setCurrentUser(data.registerUser);
          },
          onError(error: ApolloError) {
            console.log("an error occurred");
            console.log(error);
            setTimeout(() => {
              instance.logoutRedirect();
            }, 5000);
          },
        });

        //setTimeout(() => {
        //console.log('navigating...');
        //navigate('/');
        //}, 1);
      } else {
        // We have a current user, figure out which step we are on
        if (currentUser.registrationComplete) {
          navigate("/");
        } else {
          if (currentUser.registrationStep === 1) {
            // user created
            // display plan selection
          } else if (currentUser.registrationStep === 2) {
            // plan selected
            listGenres();
          }
        }
      }
    }
  }, [currentUser, hostedPageId]);

  useEffect(() => {
    if (subscriptionPlanURL !== "") {
      let iframe = document.getElementById("payForPlanFrame");
      if (iframe != null) {
        //@ts-ignore
        iframe.src = subscriptionPlanURL;
      }
    }
  }, [subscriptionPlanURL, addOnQuantity]);

  useEffect(() => {
    if (hostedPageId !== null && currentUser?.registrationStep == 3) {
      navigate("/accountSettings/subscription?hostedpage_id=" + hostedPageId);
    }
    else if (hostedPageId !== null && currentUser?.registrationStep == 1) {
      console.log("start processing registration");
      processRegistrationPlan({
        variables: {
          hostedPageId: hostedPageId,
        },
        onCompleted(data: ProcessRegistrationPlanMutation) {
          console.log("processing finished");

          if (data.processRegistrationPlan?.error) {
            toast.error(data.processRegistrationPlan?.error);
          } else {
            if (data?.processRegistrationPlan == null) {
              toast.error("An error occurred while processing your subscription");
            }
            setCurrentUser(data?.processRegistrationPlan?.user);
          }
          navigate("/register");
        },
        onError(error: ApolloError) {
          console.log("an error occurred");
          console.log(error);
          setTimeout(() => {
            instance.logoutRedirect();
          }, 5000);
        },
      });
    } else {
      if (hostedPageId !== null && currentUser?.registrationStep !== 1) {
        navigate("/register");
      }
    }
  }, [hostedPageId]);

  return (
    <>
      <div style={{ marginBottom: 10 }}>
        {registerUserLoading && (
          <div>
            <CircularProgress />
          </div>
        )}
        {registerUserData && registerUserData.registerUser?.error && (
          <div>
            <h1>Registration Error:</h1>
            <div style={{ color: "red" }}>{registerUserData.registerUser.error}</div>
          </div>
        )}
        {registerUserError && (
          <div>
            <h1>Registration Error:</h1>
            <div>{registerUserError.message}</div>
          </div>
        )}
        {currentUser?.registrationStep === 1 &&
          hostedPageId !== null && ( // process subscription
            <>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <h1>Initializing Account</h1>
                <CircularProgress />
              </div>
            </>
          )}
        {currentUser?.registrationStep === 1 &&
          subscriptionPlan === null &&
          hostedPageId === null && ( // plan selection
            <>
              <div style={{ textAlign: "center" }}>
                <h1>Select a Plan</h1>
              </div>
              <Box sx={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    position: "relative",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ position: "relative" }}>
                    <ToggleButtonGroup color="primary" value={planType} exclusive onChange={handlePlanTypeChange}>
                      <ToggleButton value={PLAN_TYPE.MONTHLY}>Monthly</ToggleButton>
                      <ToggleButton value={PLAN_TYPE.YEARLY}>Yearly</ToggleButton>
                    </ToggleButtonGroup>
                    <div id="planSaveMore" />
                  </div>
                </div>
                <PlanPricingTableComponent
                  planType={planType}
                  subscriptionPlan={subscriptionPlan}
                  setSubscriptionPlanURL={setSubscriptionPlanURLBase}
                  setSubscriptionPlan={setSubscriptionPlan}
                />
              </Box>
            </>
          )}
        {currentUser?.registrationStep === 1 &&
          subscriptionPlan !== null &&
          addOnQuantity === null && ( // select dara registration package
            <>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-evenly", width: "100%" }}>
                  <Button
                    color="secondary"
                    variant="text"
                    onClick={() => {
                      setSubscriptionPlan(null);
                      setAddOnQuantity(null);
                    }}
                  >
                    <>
                      <NavigateBack style={{ marginRight: 0 }} />
                    </>
                  </Button>
                  <div style={{ textAlign: "center" }}>
                    <h1>Exclusive Offer</h1>
                  </div>
                  <div style={{ minWidth: 64 }}></div>
                </div>
                <div style={{ marginLeft: 10, marginRight: 10 }}>
                  <WrapperDiv>
                    <div style={{ width: "100%", border: "1px solid gray", borderRadius: 22, padding: 20 }}>
                      <h2 style={{ textAlign: "center", color: "var(--theme-color)", marginTop: 0 }}>
                        {subscriptionPlan?.registration_offer?.title}
                      </h2>
                      <h3 style={{ textAlign: "center" }}>{subscriptionPlan?.registration_offer?.description}</h3>
                      <div>
                        <table style={{ display: "inline-block" }}>
                          <tbody>
                            <tr style={{ fontWeight: "bold" }}>
                              <td style={{ width: 200 }}>Number of Packages</td>
                              <td style={{ width: 150 }}>Package Dara</td>
                              <td style={{ width: 150 }}>Package Price</td>
                            </tr>
                            <tr>
                              <td>{selectedAddOnQuantity}</td>
                              <td>
                                <NumberFormat value={subscriptionPlan?.registration_offer?.dara} displayType="text" thousandSeparator={true} />
                              </td>
                              <td>${subscriptionPlan?.registration_offer?.price}</td>
                            </tr>
                          </tbody>
                        </table>
                        <h3>
                          Total Dara:{" "}
                          <NumberFormat
                            value={
                              selectedAddOnQuantity *
                              (subscriptionPlan.registration_offer?.dara == null ? 0 : subscriptionPlan.registration_offer.dara)
                            }
                            displayType="text"
                            thousandSeparator={true}
                          />
                        </h3>
                        <h3>
                          Total Cost: $
                          {(
                            selectedAddOnQuantity *
                            (subscriptionPlan.registration_offer?.price == null ? 0 : subscriptionPlan.registration_offer.price)
                          ).toFixed(2)}
                        </h3>
                        <div style={{ width: "100%", textAlign: "center" }}>
                          <span>
                            <b>Number of Packages</b>
                          </span>
                        </div>
                        <Slider
                          value={selectedAddOnQuantity}
                          min={1}
                          max={20}
                          style={{ width: "100%" }}
                          onChange={handleAddOnQuantityChange}
                          aria-label="Default"
                        />
                        <div style={{ marginTop: 20, textAlign: "center" }}>
                          <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                              skipOffer();
                            }}
                          >
                            Skip Offer
                          </Button>
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={() => {
                              addOffer();
                            }}
                            style={{ marginLeft: 50 }}
                          >
                            Add Offer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </WrapperDiv>
                </div>
              </div>
            </>
          )}
        {currentUser?.registrationStep === 1 &&
          subscriptionPlanURL !== "" &&
          addOnQuantity !== null && ( // pay for plan
            <>
              <div style={{ display: "flex", justifyContent: "space-evenly", width: "100%" }}>
                <Button
                  color="secondary"
                  variant="text"
                  onClick={() => {
                    setAddOnQuantity(null);
                  }}
                >
                  <>
                    <NavigateBack style={{ marginRight: 0 }} />
                  </>
                </Button>
                <div style={{ textAlign: "center" }}>
                  <h1>Pay for Plan</h1>
                </div>
                <div style={{ minWidth: 64 }}></div>
              </div>
              <Box sx={{ width: "100%" }}>
                <iframe id="payForPlanFrame" title="Plan Payment" style={{ minWidth: "100%", height: isMobile ? 1700 : 1300, border: "none" }} />
              </Box>
            </>
          )}
        {currentUser?.registrationStep === 2 && ( // avatar and genre selection
          <>
            <div style={{ display: "flex", justifyContent:'center', marginRight:10, marginLeft:10 }}>
              <WrapperDiv>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <h1>User Settings</h1>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {!currentUser?.avatarVersion && <Avatar alt="Avatar Image" style={{ width: 150, height: 150 }} />}
                    {currentUser?.avatarVersion && <Avatar alt="Avatar Image" src={avatarURL} style={{ width: 150, height: 150 }} />}
                    <div>
                      {widgetStatus === "ready" && (
                        <UploadWidget
                          sources={["local", "url", "unsplash", "camera"]} // set the sources available for uploading -> by default
                          // all sources are available. More information on their use can be found at
                          // https://cloudinary.com/documentation/upload_widget#the_sources_parameter
                          sourceKeys={{}} // add source keys
                          // and ID's as an object. More information on their use can be found at
                          // https://cloudinary.com/documentation/upload_widget#the_sources_parameter
                          resourceType={"image"} // optionally set with 'auto', 'image', 'video' or 'raw' -> default = 'auto'
                          cloudName={"miadara"} // your cloudinary account cloud name.
                          // Located on https://cloudinary.com/console/
                          uploadPreset={"avatar_upload"} // check that an upload preset exists and check mode is signed or unisgned
                          buttonText={"Upload Avatar"} // default 'Upload Files'
                          style={{
                            color: "white",
                            border: "none",
                            width: "120px",
                            backgroundColor: "green",
                            borderRadius: "4px",
                            height: "25px",
                          }} // inline styling only or style id='cloudinary_upload_button'
                          folder={"users/" + currentUser.userIDHash} // set cloudinary folder name to send file
                          cropping={true} // set ability to crop images -> default = true
                          // https://support.cloudinary.com/hc/en-us/articles/203062071-How-to-crop-images-via-the-Upload-Widget-#:~:text=Click%20on%20the%20%22Edit%22%20link,OK%22%20and%20Save%20the%20changes.
                          // more information here on cropping. Coordinates are returned or upload preset needs changing
                          multiple={false} // set to false as default. Allows multiple file uploading
                          // will only allow 1 file to be uploaded if cropping set to true
                          autoClose={true} // will close the widget after success. Default true
                          onSuccess={successCallBack} // add success callback -> returns result
                          onFailure={failureCallBack} // add failure callback -> returns 'response.error' + 'response.result'
                          logging={true} // logs will be provided for success and failure messages,
                          // set to false for production -> default = true
                          customPublicId={"avatar"} // set a specific custom public_id.
                          // To use the file name as the public_id use 'use_filename={true}' parameter
                          //eager={'w_400,h_300,c_pad|w_260,h_200,c_crop'} // add eager transformations -> deafult = null
                          use_filename={false} // tell Cloudinary to use the original name of the uploaded
                          // file as its public ID -> default = true,

                          /*widgetStyles={{
              palette: {
                window: '#737373',
                windowBorder: '#FFFFFF',
                tabIcon: '#FF9600',
                menuIcons: '#D7D7D8',
                textDark: '#DEDEDE',
                textLight: '#FFFFFF',
                link: '#0078FF',
                action: '#FF620C',
                inactiveTabIcon: '#B3B3B3',
                error: '#F44235',
                inProgress: '#0078FF',
                complete: '#20B832',
                sourceBg: '#909090'
              },
              fonts: {
                default: null,
                "'Fira Sans', sans-serif": {
                  url: 'https://fonts.googleapis.com/css?family=Fira+Sans',
                  active: true
                }
              }
            }} // ability to customise the style of the widget uploader*/
                          destroy={false} // will destroy the widget on completion
                          // 👇 FOR SIGNED UPLOADS ONLY 👇

                          generateSignatureUrl={`${process.env.REACT_APP_REST_URI}/GenerateCloudinaryAvatarSignature`} // pass the api
                          // endpoint for generating a signature -> check cloudinary docs and SDK's for signing uploads
                          apiKey={814698183619494} // cloudinary API key -> number format
                          accepts={"application/json"} // for signed uploads only -> default = 'application/json'
                          contentType={"application/json"} // for signed uploads only -> default = 'application/json'
                          withCredentials={false} // default = true -> check axios documentation for more information
                          unique_filename={true} // setting it to false, you can tell Cloudinary not to attempt to make
                          // the Public ID unique, and just use the normalized file name -> default = true
                        />
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", width: "100%", marginTop: 30 }}>
                      <div style={{ flexGrow: 1 }}>
                        <ToolTipComponent
                          htmlFor="selGenres"
                          label={"Genre Interests"}
                          style={{ marginBottom: 5 }}
                          tooltip={
                            isMobile
                              ? "Your genre interests help us prioritize the content we show you as well as indicating what you are interested in to other users."
                              : ""
                          }
                        />
                        <ZMulti
                          id="selGenres"
                          style={{
                            chips: {
                              background: theme.palette.primary.main,
                              color: "white",
                            },
                            multiselectContainer: {
                              width: "100%",
                              border: "1px solid gray",
                              borderRadius: 10,
                              color: theme.palette.primary.main,
                              flexGrow: 1,
                            },
                            searchBox: {
                              border: "none",
                              borderBottom: `1px solid ${theme.palette.primary.main}`,
                              borderRadius: 0,
                            },
                          }}
                          options={genres} // Options to display in the dropdown
                          //selectedValues={this.state.selectedValue} // Preselected value to persist in dropdown
                          onSelect={onSelect} // Function will trigger on select event
                          onRemove={onRemove} // Function will trigger on remove event
                          displayValue="name" // Property name to display in the dropdown options
                          placeholder={"Select a Genre"}
                          avoidHighlightFirstOption={true}
                        />
                        {!isMobile &&
                        <div>
                          <HelperTextLabel>
                            Your genre interests help us prioritize the content we show you as well as indicating what you are interested in to other users.
                          </HelperTextLabel>
                        </div>}
                      </div>
                    </div>
                    <Button color="primary" variant="contained" style={{ marginTop: 20 }} onClick={onCompleteRegistration}>
                      {processingRegistration && <CircularProgress size={20} style={{ color: "white", marginRight: 5 }} />}
                      Complete Registration
                    </Button>
                  </Box>
                </div>
              </WrapperDiv>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default RegisterPage;
